import { DateTime } from "luxon";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { BookingScheduleInterface } from "../../../features/Booking-Form/typings/booking.types";
import {
  ServiceDeliveryStatus,
  ServicesZoneBooking
} from "../../../features/Booking-Form/typings/booking-service-administration.types";

const DEFAULT_SETUP_MINUTES = 15;

export function hasActiveConferenceServiceOrder(sb: ServicesZoneBooking): boolean {
  if ((sb.qty ?? 0) > 0) {
    return true;
  }
  if ((sb.orderedQty ?? 0) > 0) {
    return true;
  }
  const status = sb.deliveryStatus;
  if (
    status === ServiceDeliveryStatus.ORDERED ||
    status === ServiceDeliveryStatus.IN_PROGRESS ||
    status === ServiceDeliveryStatus.READY
  ) {
    return true;
  }
  return false;
}

/** Largest setup time (minutes) among ordered / active conference service lines. */
export function maxOrderedConferenceServiceSetupMinutes(
  serviceBookings: ServicesZoneBooking[] | undefined
): number {
  if (!serviceBookings?.length) {
    return 0;
  }
  const ordered = serviceBookings.filter(hasActiveConferenceServiceOrder);
  if (ordered.length === 0) {
    return 0;
  }
  return Math.max(...ordered.map(sb => sb.setUpTime ?? DEFAULT_SETUP_MINUTES));
}

/**
 * While inside [bookingStart - maxSetup, bookingStart), cancel-booking and inline time edits
 * should be disabled so ordered conference services can be prepared. Compute once (e.g.
 * `useConferenceServiceSetupLock`) and pass the result to both controls.
 */
export function isScheduleTimeLockedByConferenceServiceSetup({
  schedule,
  timezone,
  now
}: {
  schedule: BookingScheduleInterface;
  timezone: string;
  now: DateTime;
}): boolean {
  if (schedule.bookingType !== BookingType.CONFERENCEZONE) {
    return false;
  }
  const maxSetup = maxOrderedConferenceServiceSetupMinutes(schedule.serviceBookings);
  if (maxSetup <= 0) {
    return false;
  }
  const bookingStart = DateTime.fromISO(schedule.startDate).setZone(timezone);
  if (!bookingStart.isValid) {
    return false;
  }
  const nowZoned = now.setZone(timezone);
  const setupWindowStart = bookingStart.minus({ minutes: maxSetup });
  if (nowZoned < setupWindowStart) {
    return false;
  }
  if (nowZoned >= bookingStart) {
    return false;
  }
  return true;
}
